import type { Track } from "./types";

/**
 * Development seed data.
 *
 * Deliberately weighted toward the catalogue Nova is built for, including the
 * cases that break generic tools: modal genres where a major/minor key answer is
 * wrong, half-time genres where tempo octave correction matters, and non-Latin
 * typography that must never render as broken character blocks.
 *
 * Replaced by live sidecar data once the scanner is wired in.
 */
const t = (
  id: string,
  title: string,
  artist: string,
  album: string | null,
  year: number | null,
  genre: string,
  subGenre: string | null,
  tonality: Track["tonality"],
  tempoBand: [number, number],
  lufs: number,
  duration: number,
  introEnd: number | null,
  ready = false
): Track => ({
  id,
  path: `${genre}/${artist} - ${title}`,
  title,
  artist,
  album,
  year,
  genre,
  subGenre,
  tonality,
  tempoBand,
  bpm: ready ? Math.round((tempoBand[0] + tempoBand[1]) / 2) : null,
  bpmConfidence: ready ? 0.93 : null,
  musicalKey: ready ? "8A" : null,
  keyConfidence: ready ? 0.88 : null,
  energy: ready ? 0.71 : null,
  energyConfidence: ready ? 0.8 : null,
  lufs,
  lra: 6.5,
  truePeak: lufs + 7.2,
  cueIn: 0,
  cueOut: duration,
  duration,
  introEnd,
});

export const SEED_TRACKS: Track[] = [
  t("1", "Àlàáfíà", "Bùrnà Bọ́y", "Twice as Tall", 2020, "Afrobeats", "Alté", "western", [96, 115], -8.2, 214, 12.4, true),
  t("2", "Log Drum Season", "Kabza De Small", "Piano Hits", 2022, "Amapiano", "Private School", "western", [108, 120], -7.8, 428, 24.1),
  t("3", "Kumerica Anthem", "Yaw Tog", null, 2021, "Drill", "Asakaa", "western", [134, 148], -6.4, 186, 8.2),
  t("4", "Ẹ̀gbọ́n Mi", "King Sunny Adé", "Juju Classics", 1982, "Juju", "Jùjú", "modal", [96, 132], -11.3, 512, 31.7),
  t("5", "Sound Sultan Tribute", "Wizkid", "Made in Lagos", 2020, "Afrobeats", null, "western", [96, 115], -9.1, 197, 15.3, true),
  t("6", "Ìyàwó Mi", "Wasiu Ayinde", "Fuji Garbage", 1994, "Fuji", null, "modal", [96, 144], -12.6, 604, 42.0),
  t("7", "Gqom Nights", "DJ Lag", "Durban Sound", 2019, "Gqom", null, "western", [118, 132], -6.9, 322, 16.8),
  t("8", "Sweet Mother", "Prince Nico Mbarga", "Highlife Gold", 1976, "Highlife", null, "modal", [96, 140], -13.4, 371, 22.5),
  t("9", "Coupé Serré", "DJ Arafat", null, 2013, "Coupé-Décalé", null, "western", [116, 134], -7.2, 265, 11.1),
  t("10", "Singeli Speed", "Sisso", "Sounds of Sisso", 2021, "Singeli", null, "western", [180, 300], -5.8, 178, 4.2),
  t("11", "Bongo Love", "Diamond Platnumz", "A Boy From Tandale", 2018, "Bongo Flava", null, "western", [88, 112], -8.7, 232, 18.9, true),
  t("12", "Gengetone Riot", "Sailors", null, 2019, "Gengetone", null, "western", [92, 110], -7.5, 208, 9.4),
  t("13", "完全に違う", "Yasuko Agawa", "City Pop Vol. 2", 1983, "Pop", null, "western", [90, 132], -10.2, 289, 20.1),
  t("14", "أغنية جميلة", "Amr Diab", "Sahran", 2020, "Pop", "Latin Pop", "western", [88, 130], -9.6, 251, 17.2),
  t("15", "Soukous Fever", "Kanda Bongo Man", "Kwassa Kwassa", 1989, "Soukous", null, "western", [118, 150], -11.8, 398, 26.3),
  t("16", "Kwaito Revival", "Mandoza", null, 2000, "Kwaito", null, "western", [98, 118], -8.9, 244, 13.7),
  t("17", "Taarab Nights", "Bi Kidude", "Zanzibar", 1988, "Taarab", null, "modal", [84, 128], -14.1, 447, 35.8),
  t("18", "Mbalax Drive", "Youssou N'Dour", "Set", 1990, "Mbalax", null, "modal", [116, 148], -10.7, 336, 19.4),
  t("19", "Vibes 🎵🔥", "Rema", "Rave & Roses", 2022, "Afrobeats", "Afro-Fusion", "western", [92, 118], -7.1, 189, 10.8, true),
  t("20", "Kuduro Blast", "Cabo Snoop", null, 2011, "Kuduro", null, "western", [128, 150], -6.2, 221, 7.9),
  t("21", "Zim Sungura", "Alick Macheso", "Zvakanaka", 2005, "Sungura", null, "modal", [112, 142], -12.2, 462, 28.6),
  t("22", "Maskandi Roots", "Phuzekhemisi", null, 1998, "Mbaqanga", "Maskandi", "modal", [100, 140], -13.9, 389, 24.9),
  t("23", "Station ID — Morning", "Imaging", null, 2024, "Spoken Word", null, "western", [60, 120], -16.0, 8, null, true),
  t("24", "Afro House Sunrise", "Black Coffee", "Subconsciously", 2021, "Afro House", null, "western", [115, 128], -8.4, 415, 33.2, true),
];
